/**
 * APIs y tipos añadidos por el sprint multi-centro: tags, profile,
 * platform y los grants admin. Separados de `client.ts` solo para
 * respetar el límite de 500 líneas por fichero.
 */
import { apiFetch } from './client';
import type {
  Booking,
  BookingStatus,
  Customer,
  EmailPreviewDto,
  EmailSettingDto,
  EmailType,
  PaginatedResponse,
} from './types';

export interface CenterAdmin {
  id: string;
  email: string;
  role: 'superadmin' | 'center_admin' | 'staff';
}

export interface Tag {
  id: string;
  name: string;
  slug: string;
}

export interface MyProfile {
  email: string;
  name: string | null;
  lastName: string | null;
  phone: string | null;
  jobTitle: string | null;
  bio: string | null;
  credentials: string[] | null;
  photoUrl: string | null;
  functions: string[] | null;
  websiteUrl: string | null;
}

export interface PublicDirectoryInfo {
  url: string;
  allowedOrigins: string[];
  editableHint: string;
}

export const centerAdminExtrasApi = {
  listAdmins: (centerId: string) =>
    apiFetch<CenterAdmin[]>(`/api/admin/centers/${centerId}/admins`),
  grantAdmin: (centerId: string, userId: string) =>
    apiFetch<unknown>(`/api/admin/centers/${centerId}/admins`, {
      method: 'POST',
      body: JSON.stringify({ userId }),
    }),
  revokeAdmin: (centerId: string, userId: string) =>
    apiFetch<undefined>(`/api/admin/centers/${centerId}/admins/${userId}`, { method: 'DELETE' }),
  listTagIds: (centerId: string) =>
    apiFetch<{ items: string[] }>(`/api/admin/centers/${centerId}/tags`),
};

export const adminTagsApi = {
  list: () => apiFetch<{ items: Tag[] }>('/api/admin/tags'),
  create: (name: string) =>
    apiFetch<Tag>('/api/admin/tags', { method: 'POST', body: JSON.stringify({ name }) }),
  update: (id: string, name: string) =>
    apiFetch<Tag>(`/api/admin/tags/${id}`, { method: 'PUT', body: JSON.stringify({ name }) }),
  delete: (id: string) =>
    apiFetch<undefined>(`/api/admin/tags/${id}`, { method: 'DELETE' }),
};

export const adminPlatformApi = {
  publicDirectoryInfo: () =>
    apiFetch<PublicDirectoryInfo>('/api/admin/platform/public-directory-info'),
};

export const adminStaffPhotoApi = {
  upload: async (staffId: string, file: File): Promise<{ photoUrl: string }> => {
    const form = new FormData();
    form.append('photo', file);
    return apiFetch<{ photoUrl: string }>(`/api/admin/staff/${staffId}/profile/photo`, {
      method: 'PUT',
      body: form,
    });
  },
  delete: (staffId: string) =>
    apiFetch<{ photoUrl: null }>(`/api/admin/staff/${staffId}/profile/photo`, { method: 'DELETE' }),
};

export const adminProfileApi = {
  get: () => apiFetch<MyProfile>('/api/admin/me/profile'),
  update: (data: Partial<Omit<MyProfile, 'email' | 'photoUrl'>>) =>
    apiFetch<Omit<MyProfile, 'email'>>('/api/admin/me/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  uploadPhoto: async (file: File): Promise<{ photoUrl: string }> => {
    const form = new FormData();
    form.append('photo', file);
    return apiFetch<{ photoUrl: string }>('/api/admin/me/profile/photo', {
      method: 'POST',
      body: form,
    });
  },
  deletePhoto: () =>
    apiFetch<{ photoUrl: null }>('/api/admin/me/profile/photo', { method: 'DELETE' }),
};

// ---------------------------------------------------------------------------
// PR #4 — admin create bookings + filtros multi-centro.
//
// Estos wrappers tipados viven aquí (no en `client.ts`) por dos razones:
//  1. `client.ts` ya ronda el límite de 500 líneas — `max-lines` de ESLint.
//  2. Toda la superficie nueva del PR #4 queda agrupada para facilitar
//     reviews y futura extracción.
//
// El método legacy `adminBookingsApi.list(Record<string, string>)` sigue
// existiendo en `client.ts` porque lo consumen `useBookings()` y
// `useCustomerBookings()`. Los nuevos métodos son adicionales (typed).
// ---------------------------------------------------------------------------

export interface AdminBookingListParams {
  centerId?: string;
  staffId?: string;
  status?: BookingStatus;
  /** ISO date (YYYY-MM-DD) or full datetime — backend normaliza. */
  from?: string;
  to?: string;
  q?: string;
  customerId?: string;
  page?: number;
  perPage?: number;
}

/** Body of `POST /api/admin/bookings`. Mirror del contrato PR #4. */
export interface AdminCreateBookingBody {
  centerId: string;
  customerId?: string;
  customer?: { name: string; email: string; phone?: string };
  serviceId: string;
  /** `null` = pool (first-fit, "cualquier profesional"). */
  staffId?: string | null;
  /** ISO datetime (UTC). */
  startTime: string;
  customerNote?: string;
  /** Default true. Pasar `false` salta el email de confirmación. */
  notifyCustomer?: boolean;
}

function buildBookingListQuery(params: AdminBookingListParams): string {
  const qs = new URLSearchParams();
  if (params.centerId)    qs.set('centerId', params.centerId);
  if (params.staffId)     qs.set('staffId', params.staffId);
  if (params.status)      qs.set('status', params.status);
  if (params.from)        qs.set('from', params.from);
  if (params.to)          qs.set('to', params.to);
  if (params.q)           qs.set('q', params.q);
  if (params.customerId)  qs.set('customerId', params.customerId);
  if (params.page !== undefined)    qs.set('page', String(params.page));
  if (params.perPage !== undefined) qs.set('perPage', String(params.perPage));
  return qs.toString();
}

export const adminBookingsExtrasApi = {
  /**
   * Listado tipado del admin. Reemplaza al `adminBookingsApi.list()` no-tipado
   * cuando se quieran usar los nuevos filtros (`centerId`, `staffId`) sin
   * stringificar manualmente. La respuesta es la `PaginatedDto<Booking>` real
   * del backend (`data, total, page, perPage, totalPages`).
   */
  list: (params: AdminBookingListParams = {}) =>
    apiFetch<PaginatedResponse<Booking>>(`/api/admin/bookings?${buildBookingListQuery(params)}`),

  /** Crea una reserva desde el admin. Solo `superadmin | center_admin` (server-side). */
  create: (body: AdminCreateBookingBody) =>
    apiFetch<Booking>('/api/admin/bookings', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
};

// ---------------------------------------------------------------------------
// Customer autocomplete — reusa `GET /api/admin/customers`.
//
// El backend devuelve `PaginatedDto<Customer>`; aquí proyectamos a una
// firma de "search" cómoda para autocomplete (`{ items: Customer[] }`).
// ---------------------------------------------------------------------------

export const adminCustomersSearchApi = {
  /**
   * Busca clientes por nombre/email/teléfono. `centerId` opcional acota la
   * búsqueda (sólo aplica si el caller es superadmin; staff/center_admin lo
   * tienen forzado en backend).
   */
  search: async (
    q: string,
    centerId?: string,
    perPage = 10,
  ): Promise<{ items: Customer[] }> => {
    const qs = new URLSearchParams();
    qs.set('q', q);
    qs.set('perPage', String(perPage));
    if (centerId) qs.set('centerId', centerId);
    const resp = await apiFetch<PaginatedResponse<Customer>>(
      `/api/admin/customers?${qs.toString()}`,
    );
    return { items: resp.data };
  },
};

// ---------------------------------------------------------------------------
// Public availability — reusado por el BookingFormDrawer.
//
// El endpoint vive en `/api/public/bookings/availability` (sin auth, CORS
// permisivo). Lo invocamos vía `apiFetch` para reaprovechar request-id y
// manejo de errores, pero NO necesita `Authorization`. Como la ruta es
// pública el header se ignora a nivel server, sin efectos colaterales.
//
// Acepta un único `date` (YYYY-MM-DD) — para mostrar el dropdown de slots
// del drawer eso es exactamente lo que necesitamos.
// ---------------------------------------------------------------------------

export interface PublicSlotDto {
  /** ISO datetime UTC. */
  startIso: string;
  endIso: string;
}

// ---------------------------------------------------------------------------
// PR #8 — Email config (master switches + preview)
//
// Endpoints superadmin:
//  - GET  /api/admin/system/emails/settings           → lista los 8 tipos
//  - PUT  /api/admin/system/emails/settings/:type     → toggle (422 si magic_link)
//  - GET  /api/admin/system/emails/preview/:type      → render con mock data
//
// Vive aquí (no en `client.ts`) por el límite max-lines 500.
// ---------------------------------------------------------------------------

export const adminEmailsApi = {
  settings: () =>
    apiFetch<{ items: EmailSettingDto[] }>('/api/admin/system/emails/settings'),
  setEnabled: (type: EmailType, enabled: boolean) =>
    apiFetch<{ type: EmailType; enabled: boolean }>(
      `/api/admin/system/emails/settings/${type}`,
      { method: 'PUT', body: JSON.stringify({ enabled }) },
    ),
  preview: (type: EmailType) =>
    apiFetch<EmailPreviewDto>(`/api/admin/system/emails/preview/${type}`),
};

export const adminAvailabilityApi = {
  /**
   * Lista los slots libres para `(centerId, serviceId, staffId?, date)`.
   * `staffId === null/undefined` = pool (first-fit en backend).
   */
  list: (
    centerId: string,
    serviceId: string,
    staffId: string | null | undefined,
    date: string,
  ): Promise<readonly PublicSlotDto[]> => {
    const qs = new URLSearchParams();
    qs.set('centerId', centerId);
    qs.set('serviceId', serviceId);
    if (staffId) qs.set('staffId', staffId);
    qs.set('date', date);
    return apiFetch<readonly PublicSlotDto[]>(
      `/api/public/bookings/availability?${qs.toString()}`,
    );
  },
};
